import { fetchAndUpdateSessionExpiration as fetch } from 'platform/utilities/api';
import environment from 'platform/utilities/environment';

import manifest from '../manifest.json';
import { OTHER_VHA_MEDICAL_FACILITY_OPTION } from '../definitions/facilities';

const FACILITIES_API_URL = `${environment.API_URL}/facilities_api/v2/va`;
const FACILITIES_PER_PAGE = 150;

const facilitiesByStateCache = new Map();

const normalizeSearchText = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ');

const sortByLabel = (firstOption, secondOption) =>
  firstOption.label.localeCompare(secondOption.label, undefined, {
    sensitivity: 'base',
    numeric: true,
  });

const getPhysicalAddress = attributes =>
  attributes?.address?.physical || attributes?.address?.mailing || {};

const buildFacilityOption = facility => {
  const attributes = facility?.attributes || {};
  const { name } = attributes;
  if (!name) {
    return null;
  }
  const address = getPhysicalAddress(attributes);
  const location = [address.city, address.state].filter(Boolean).join(', ');

  return {
    id: String(
      facility?.id ||
        attributes.id ||
        attributes.uniqueId ||
        `${name}-${location}`,
    ),
    label: location ? `${name} (${location})` : name,
  };
};

export const normalizeVhaFacilitiesResponse = response => {
  const facilities = Array.isArray(response?.data) ? response.data : [];
  const uniqueOptions = new Map();

  facilities.forEach(facility => {
    const attributes = facility?.attributes || {};
    const facilityId = String(facility?.id || attributes.id || '');
    const isHealthFacility =
      attributes.facilityType === 'va_health_facility' ||
      facilityId.startsWith('vha_');
    if (!isHealthFacility || attributes.mobile) return;

    const option = buildFacilityOption(facility);
    if (option) {
      uniqueOptions.set(option.id, option);
    }
  });
  return [...uniqueOptions.values()].sort(sortByLabel);
};

const updateToken = response => {
  const token = response?.headers?.get?.('X-CSRF-Token');
  if (token && token !== localStorage.getItem('csrfToken')) {
    localStorage.setItem('csrfToken', token);
  }
};

const requestFacilitiesPage = async (state, page) => {
  const response = await fetch(FACILITIES_API_URL, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Key-Inflection': 'camel',
      'x-csrf-token': localStorage.getItem('csrfToken'),
      'Source-App-Name': manifest.entryName,
    },
    method: 'POST',
    body: JSON.stringify({
      type: 'health',
      state,
      page,
      // eslint-disable-next-line camelcase
      per_page: FACILITIES_PER_PAGE,
      mobile: false,
    }),
  });
  if (!response.ok) {
    throw new Error(
      `Facilities API request failed with status ${response.status}`,
    );
  }
  updateToken(response);
  return response.json();
};

const getTotalPages = response => {
  const totalPages = Number(response?.meta?.pagination?.totalPages ?? 1);
  return Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1;
};

const requestAllFacilitiesForState = async state => {
  const firstPage = await requestFacilitiesPage(state, 1);
  const totalPages = getTotalPages(firstPage);

  if (totalPages === 1) {
    return normalizeVhaFacilitiesResponse(firstPage);
  }

  const remainingRequests = Array.from({ length: totalPages - 1 }, (_, index) =>
    requestFacilitiesPage(state, index + 2),
  );

  const remainingPages = await Promise.all(remainingRequests);
  const uniqueOptions = new Map();

  [firstPage, ...remainingPages].forEach(response => {
    normalizeVhaFacilitiesResponse(response).forEach(option => {
      uniqueOptions.set(option.id, option);
    });
  });

  return [...uniqueOptions.values()].sort(sortByLabel);
};

export const fetchVhaFacilitiesByState = state => {
  const normalizedState = String(state || '')
    .trim()
    .toUpperCase();

  if (!/^[A-Z]{2}$/.test(normalizedState)) {
    return Promise.resolve([]);
  }

  if (!facilitiesByStateCache.has(normalizedState)) {
    const request = requestAllFacilitiesForState(normalizedState).catch(
      error => {
        facilitiesByStateCache.delete(normalizedState);
        throw error;
      },
    );

    facilitiesByStateCache.set(normalizedState, request);
  }

  return facilitiesByStateCache.get(normalizedState);
};

const filterFacilityOptions = (options, inputValue) => {
  const query = normalizeSearchText(inputValue);

  if (!query) {
    return options;
  }

  const searchableOptions = options.map(option => ({
    option,
    normalizedLabel: normalizeSearchText(option.label),
  }));

  const startsWithMatches = searchableOptions.filter(({ normalizedLabel }) =>
    normalizedLabel.startsWith(query),
  );

  const wordStartsWithMatches = searchableOptions.filter(
    searchableOption =>
      !startsWithMatches.includes(searchableOption) &&
      searchableOption.normalizedLabel.includes(` ${query}`),
  );

  const containsMatches = searchableOptions.filter(
    searchableOption =>
      !startsWithMatches.includes(searchableOption) &&
      !wordStartsWithMatches.includes(searchableOption) &&
      searchableOption.normalizedLabel.includes(query),
  );

  return [
    ...startsWithMatches,
    ...wordStartsWithMatches,
    ...containsMatches,
  ].map(({ option }) => option);
};

export const getVhaMedicalFacilityOptions = async (
  inputValue,
  { state } = {},
) => {
  const facilities = await fetchVhaFacilitiesByState(state);
  const matches = filterFacilityOptions(facilities, inputValue);

  return [...matches, OTHER_VHA_MEDICAL_FACILITY_OPTION];
};

export const clearVhaFacilityCache = () => {
  facilitiesByStateCache.clear();
};
